import { Box, ChatInput, KdsChatMessage } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { useAtomValue } from "jotai";
import { useEffect, useRef, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ModalAddBriefToOrder } from "@/modals/openBrief/ModalAddBriefToOrder";
import { isBriefWithAIAtom } from "@/store";
import { LoadingMessage, WelcomeHeader } from "./_partials";
import { type AIChatProps } from "./AIChat.types";

export const AIChat = ({
  setInitialAIValues,
  latestFields,
  aiChatTools,
}: AIChatProps) => {
  const [message, setMessage] = useState("");
  const [isModalOpen, setModalIsOpen] = useState(false);

  const [initialExpression, setInitialExpression] = useState("");

  const isBriefWithAI = useAtomValue(isBriefWithAIAtom);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslate();

  const {
    messages,
    sendMessage,
    isWaitingResponse,
    initialAIValues,
    setLatestFields,
  } = aiChatTools;

  const handleSubmitMessage = () => {
    if (!message?.length) {
      return;
    }

    if (!initialExpression?.length) {
      setInitialExpression(message);
    }

    if (!isBriefWithAI) {
      setModalIsOpen(true);
    } else {
      setMessage("");
      sendMessage(message);
    }
  };

  useEffect(() => {
    setLatestFields(latestFields);
  }, [latestFields]);

  useEffect(() => {
    if (initialAIValues) {
      setInitialAIValues(initialAIValues);
    }
  }, [initialAIValues]);

  useEffect(() => {
    messagesContainerRef?.current?.scrollTo({
      behavior: "smooth",
      left: 0,
      top: messagesContainerRef?.current?.scrollHeight,
    });
  }, [messages]);

  return (
    <Box p="sp-md" maxH="100%" w="100%">
      <div className="flex flex-col min-h-full justify-between max-h-full gap-sp-md py-sp-xl px-sp-md">
        <div
          ref={messagesContainerRef}
          id="message-container"
          className="flex flex-col gap-sp-md max-h-full overflow-y-auto overflow-x-hidden"
        >
          {!messages.length && <WelcomeHeader />}

          {messages.map((msg) => (
            <KdsChatMessage
              type={msg.sentByUser ? "user" : "answer"}
              key={msg.id}
            >
              <Markdown remarkPlugins={[remarkGfm]}>
                {msg.message.replace("**", "*")}
              </Markdown>
            </KdsChatMessage>
          ))}

          {isWaitingResponse && <LoadingMessage />}
        </div>

        <ChatInput
          isDisabled={isWaitingResponse}
          placeholder={t("openBrief.sendPlaceholder")}
          maxLength={5000}
          onChange={setMessage}
          value={message}
          onSubmit={handleSubmitMessage}
        />

        {isBriefWithAI === false && (
          <ModalAddBriefToOrder
            isOpen={isModalOpen}
            onOpenChange={() => setModalIsOpen(!isModalOpen)}
            values={{
              initialExpression,
            }}
          />
        )}
      </div>
    </Box>
  );
};
